import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Heart, X, Send, Sparkles, MapPin, Ruler, IndianRupee, Star, ImageOff,
  Check, Search, ChevronLeft, ChevronRight, Plus, FolderPlus, Trash2, Pencil,
} from "lucide-react";

import { api } from "../lib/api";
import { Sheet, Skeleton, Empty } from "../ui";

/**
 * Inspirations — the idea feed, shaped like Pinterest.
 *
 * WHY THIS IS NO LONGER SEGMENTED BY ROOM, HAVING ARGUED THAT IT SHOULD BE
 * -----------------------------------------------------------------------
 * The previous version broke the feed into room sections with a chip rail that
 * was an INDEX, not a filter. That was right when it was written: the CMS had
 * tagged zero of 99 images, so a filter would have had nothing to filter, and
 * sections at least gave the page some shape.
 *
 * 96 of 99 are tagged now, across 14 rooms — about seven pictures each. Cut
 * into fourteen sections that is a heading every seven pins, which is not a
 * feed, it is a table of contents. So:
 *
 *   · ONE continuous mixed grid, interleaved across projects, which is what
 *     makes a browse feed work — you see a kitchen you were not looking for.
 *   · The rail FILTERS now. Tap "Modular Kitchen" and you get kitchens. That
 *     is what the chips look like they should do; scroll-jumping while the
 *     chip highlighted was a small surprise every single time.
 *   · Search across room, style, project name, city, scope and materials.
 *
 * BOARDS
 * ------
 * Saving to one flat list is not how anybody collects pictures. Boards are the
 * primitive: "Kitchen", "Must have", "Maybe later". They are the client's own
 * — the studio can read them and cannot edit them — and three named boards are
 * a far better brief than forty loose hearts.
 *
 * A first save goes straight into "All ideas" with one tap. Asking which board
 * on the very first heart is how you teach someone not to press the heart; the
 * picker is a long-press, or the button in the pin detail.
 *
 * THE GRID IS CSS COLUMNS
 * -----------------------
 * Pins keep their natural aspect ratio and the columns stagger. A CSS grid
 * would crop or letterbox every picture, which for a portfolio is the one
 * thing you cannot do. The cost is that reading order runs down-then-across;
 * for an unranked browse feed nobody notices.
 */

const FEED_URL = "/api/design-ideas.php";
const BATCH = 30;
const ALL = "__all";
const SAVED = "__saved";

export function InspirationsSheet({ ctx, onClose }) {
  const feed = useFeed();
  const saves = useSaves();

  const [filter, setFilter] = useState(ALL);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(BATCH);
  const [lightbox, setLightbox] = useState(null);
  const [picker, setPicker] = useState(null);
  const [notice, setNotice] = useState("");

  const sentinel = useRef(null);

  /* Interleaved, not grouped: project A shot 1, project B shot 1, project A
     shot 2… Without this the feed is eight solid blocks of one home each,
     which reads as eight galleries rather than one feed. */
  const pins = useMemo(() => {
    const lists = (feed.data?.projects || []).map((p) =>
      (p.shots || []).map((s) => ({ url: s.url, room: s.room || "", project: p }))
    );
    const out = [];
    for (let i = 0; ; i++) {
      let any = false;
      for (const l of lists) if (l[i]) { out.push(l[i]); any = true; }
      if (!any) break;
    }
    return out;
  }, [feed.data]);

  const rooms = feed.data?.filters?.rooms || [];

  const asPins = (rows) =>
    rows.map((s) => ({
      url: s.image_url,
      room: s.room,
      project: { name: s.source_name, style: s.source_style, slug: s.source_slug },
    }));

  const visible = useMemo(() => {
    let list = pins;

    if (filter === SAVED) list = asPins(saves.list);
    else if (filter.startsWith("board:")) {
      const id = Number(filter.slice(6));
      list = asPins(saves.list.filter((s) => Number(s.board_id) === id));
    } else if (filter !== ALL) {
      list = pins.filter((p) => p.room === filter);
    }

    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const pr = p.project || {};
      return [p.room, pr.name, pr.style, pr.city, pr.location, pr.bhk,
              ...(pr.materials || []), ...(pr.scope || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [pins, saves.list, filter, query]);

  useEffect(() => { setShown(BATCH); }, [filter, query]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || shown >= visible.length || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (e) => { if (e[0].isIntersecting) setShown((n) => Math.min(visible.length, n + BATCH)); },
      { rootMargin: "700px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, visible.length]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(""), 5000); };

  async function share(boardId) {
    try {
      const r = await api.post("/portal/inspirations/share", boardId ? { board_id: boardId } : {});
      flash(`Sent ${r.count} picture${r.count === 1 ? "" : "s"} to your designer — ${r.ticket_no}.`);
    } catch (e) { flash(e.message); }
  }

  const boardOf = (url) => saves.list.find((s) => s.image_url === url)?.board_id ?? null;
  const boardName = (id) => saves.boards.find((b) => Number(b.id) === Number(id))?.name || "";

  const chips = [
    { key: ALL, label: "All ideas" },
    ...(saves.list.length ? [{ key: SAVED, label: "Saved", heart: true, n: saves.list.length }] : []),
    ...saves.boards.map((b) => ({ key: `board:${b.id}`, label: b.name, n: b.count })),
    ...rooms.map((r) => ({ key: r, label: r })),
  ];

  const showingBoard = filter.startsWith("board:") ? Number(filter.slice(6)) : null;

  return (
    <Sheet title="Ideas" onClose={onClose}>
      <div className="insp-search">
        <Search size={15} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search kitchens, teak, Kokapet…"
          aria-label="Search ideas"
        />
        {query && <button onClick={() => setQuery("")} aria-label="Clear search"><X size={14} /></button>}
      </div>

      <nav className="insp-rail" aria-label="Filter ideas">
        {chips.map((c) => (
          <button
            key={c.key}
            className={"insp-chip" + (filter === c.key ? " on" : "") + (c.heart ? " saved" : "")}
            onClick={() => setFilter(c.key)}
          >
            {c.heart && <Heart size={12} fill="currentColor" />}
            {c.label}
            {c.n != null && <i>{c.n}</i>}
          </button>
        ))}
        <button className="insp-chip ghost" onClick={() => setPicker({ mode: "new" })}>
          <FolderPlus size={13} /> New board
        </button>
      </nav>

      {notice && <div className="msg-ok">{notice}</div>}

      {showingBoard != null && (
        <BoardBar
          board={saves.boards.find((b) => Number(b.id) === showingBoard)}
          onShare={() => share(showingBoard)}
          onRename={(name) => saves.renameBoard(showingBoard, name)}
          onDelete={async () => {
            await saves.deleteBoard(showingBoard);
            setFilter(ALL);
            flash("Board removed. The pictures are still in Saved.");
          }}
        />
      )}

      {filter === SAVED && saves.list.length > 0 && (
        <div className="insp-boardbar">
          <span>{saves.list.length} saved</span>
          <button className="insp-send" onClick={() => share(0)}>
            <Send size={13} /> Send to my designer
          </button>
        </div>
      )}

      {feed.loading && (
        <div className="pin-grid">
          {[220, 150, 190, 260, 170, 210].map((h, i) => <Skeleton key={i} h={h} r={14} />)}
        </div>
      )}

      {feed.error && (
        <Empty
          icon={ImageOff}
          title="Ideas are offline right now"
          note="The gallery could not be reached. Your own designs are on the Designs tab."
          action={<button className="btn" onClick={feed.reload}>Try again</button>}
        />
      )}

      {!feed.loading && !feed.error && visible.length === 0 && (
        <Empty
          icon={Sparkles}
          title={
            query ? `Nothing matches “${query}”`
              : filter === SAVED ? "Nothing saved yet"
                : showingBoard != null ? "This board is empty"
                  : "Nothing in the gallery yet"
          }
          note={filter === ALL && !query ? undefined : "Tap the heart on any picture to keep it."}
          action={
            filter !== ALL || query
              ? <button className="btn" onClick={() => { setFilter(ALL); setQuery(""); }}>Show everything</button>
              : undefined
          }
        />
      )}

      {visible.length > 0 && (
        <>
          <div className="pin-grid">
            {visible.slice(0, shown).map((pin, i) => (
              <Pin
                key={pin.url + i}
                pin={pin}
                saved={saves.has(pin.url)}
                board={boardName(boardOf(pin.url))}
                onToggle={() => saves.toggle(pin)}
                onFile={() => setPicker({ mode: "file", pin })}
                onOpen={() => setLightbox(i)}
              />
            ))}
          </div>
          <div ref={sentinel} style={{ height: 1 }} />
          {shown < visible.length && (
            <p className="muted" style={{ textAlign: "center", fontSize: ".8rem" }}>Loading more…</p>
          )}
        </>
      )}

      {lightbox !== null && visible[lightbox] && (
        <Lightbox
          pins={visible}
          index={lightbox}
          onIndex={setLightbox}
          allPins={pins}
          saved={saves.has(visible[lightbox].url)}
          board={boardName(boardOf(visible[lightbox].url))}
          onToggle={() => saves.toggle(visible[lightbox])}
          onFile={() => setPicker({ mode: "file", pin: visible[lightbox] })}
          onClose={() => setLightbox(null)}
          onAsk={ctx.ask}
        />
      )}

      {picker && (
        <BoardPicker
          mode={picker.mode}
          pin={picker.pin}
          boards={saves.boards}
          currentBoard={picker.pin ? boardOf(picker.pin.url) : null}
          onClose={() => setPicker(null)}
          onCreate={saves.createBoard}
          onFile={saves.fileTo}
          onDone={(msg) => { setPicker(null); if (msg) flash(msg); }}
        />
      )}
    </Sheet>
  );
}

/* ---- one pin ------------------------------------------------------------- */

/**
 * Save is a button in the corner, and a DOUBLE TAP anywhere on the picture.
 * The double tap is the one everybody already knows from every photo feed
 * there is, and it is why the corner button can stay small enough not to
 * cover the picture it sits on. A long press opens the board picker.
 */
function Pin({ pin, saved, board, onToggle, onFile, onOpen }) {
  const [burst, setBurst] = useState(false);
  const lastTap = useRef(0);
  const press = useRef(null);
  const held = useRef(false);

  const tap = () => {
    if (held.current) { held.current = false; return; }
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      if (!saved) { onToggle(); setBurst(true); setTimeout(() => setBurst(false), 620); }
      return;
    }
    lastTap.current = now;
    setTimeout(() => { if (lastTap.current === now) { lastTap.current = 0; onOpen(); } }, 300);
  };

  return (
    <figure className="pin">
      <button
        className="pin-img"
        onClick={tap}
        onContextMenu={(e) => { e.preventDefault(); held.current = true; onFile(); }}
        onPointerDown={() => { press.current = setTimeout(() => { held.current = true; onFile(); }, 550); }}
        onPointerUp={() => clearTimeout(press.current)}
        onPointerLeave={() => clearTimeout(press.current)}
        aria-label={pin.project?.name || "Open this idea"}
      >
        <img src={pin.url} alt="" loading="lazy" decoding="async" />
        {burst && <span className="pin-burst" aria-hidden="true"><Heart size={54} fill="currentColor" /></span>}
      </button>

      {board && <span className="pin-board">{board}</span>}

      <button
        className={"pin-heart" + (saved ? " on" : "")}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        aria-label={saved ? "Remove from saved" : "Save this idea"}
        aria-pressed={saved}
      >
        <Heart size={15} fill={saved ? "currentColor" : "none"} />
      </button>
    </figure>
  );
}

/* ---- a board's own header ------------------------------------------------ */

function BoardBar({ board, onShare, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(board?.name || "");
  useEffect(() => { setName(board?.name || ""); }, [board?.name]);
  if (!board) return null;

  const commit = () => { const n = name.trim(); if (n) onRename(n); setEditing(false); };

  return (
    <div className="insp-boardbar">
      {editing ? (
        <>
          <input
            className="insp-rename"
            value={name}
            maxLength={80}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            aria-label="Board name"
          />
          <button className="insp-send" onClick={commit}><Check size={13} /> Save</button>
        </>
      ) : (
        <>
          <span>{board.name} · {board.count}</span>
          <button className="insp-icon" onClick={() => setEditing(true)} aria-label="Rename board">
            <Pencil size={13} />
          </button>
          <button
            className="insp-icon danger"
            onClick={() => {
              if (window.confirm(`Remove the "${board.name}" board? The pictures stay in Saved.`)) onDelete();
            }}
            aria-label="Delete board"
          >
            <Trash2 size={13} />
          </button>
          <button className="insp-send" onClick={onShare}><Send size={13} /> Send</button>
        </>
      )}
    </div>
  );
}

/* ---- board picker -------------------------------------------------------- */

function BoardPicker({ mode, pin, boards, currentBoard, onClose, onCreate, onFile, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function create() {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    const board = await onCreate(n);
    if (pin && board?.id) await onFile(pin, board.id);
    onDone(pin ? `Saved to “${n}”.` : `Board “${n}” created.`);
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="insp-picker" role="dialog" aria-modal="true" aria-label="Choose a board">
        <span className="sheet-grab" aria-hidden="true" />
        <h3>{mode === "new" ? "New board" : "Save to a board"}</h3>

        {mode === "file" && (
          <div className="insp-picker-list">
            <button
              className={"insp-picker-row" + (!currentBoard ? " on" : "")}
              onClick={async () => { await onFile(pin, 0); onDone("Moved to All ideas."); }}
            >
              <span className="insp-picker-cover"><Heart size={15} /></span>
              <span className="insp-picker-name">All ideas</span>
              {!currentBoard && <Check size={15} />}
            </button>
            {boards.map((b) => (
              <button
                key={b.id}
                className={"insp-picker-row" + (Number(currentBoard) === Number(b.id) ? " on" : "")}
                onClick={async () => { await onFile(pin, b.id); onDone(`Saved to “${b.name}”.`); }}
              >
                <span
                  className="insp-picker-cover"
                  style={b.cover ? { backgroundImage: `url(${b.cover})` } : undefined}
                />
                <span className="insp-picker-name">{b.name}</span>
                <i>{b.count}</i>
                {Number(currentBoard) === Number(b.id) && <Check size={15} />}
              </button>
            ))}
          </div>
        )}

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="bd-name">{mode === "new" ? "Name it" : "Or make a new board"}</label>
          <input
            id="bd-name"
            value={name}
            maxLength={80}
            placeholder="Kitchen"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn primary" style={{ flex: 1 }} onClick={create} disabled={busy || !name.trim()}>
            <Plus size={15} /> Create
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ---- the pin detail ------------------------------------------------------ */

function Lightbox({ pins, index, onIndex, allPins, saved, board, onToggle, onFile, onClose, onAsk }) {
  const pin = pins[index];
  const p = pin.project || {};
  const touch = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && index < pins.length - 1) onIndex(index + 1);
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, pins.length, onClose, onIndex]);

  /* "More like this" — same room first, then the rest of the same home. This
     is the whole reason a pin gets a page rather than being a modal image:
     one kitchen you like should lead to five more. */
  const more = useMemo(() => {
    const seen = new Set([pin.url]);
    const out = [];
    for (const c of allPins) {
      if (out.length >= 12) break;
      if (!seen.has(c.url) && pin.room && c.room === pin.room) { out.push(c); seen.add(c.url); }
    }
    for (const c of allPins) {
      if (out.length >= 12) break;
      if (!seen.has(c.url) && p.slug && c.project?.slug === p.slug) { out.push(c); seen.add(c.url); }
    }
    return out;
  }, [allPins, pin, p.slug]);

  const facts = [
    [MapPin, p.city || p.location],
    [Ruler, [p.bhk, p.sqft ? `${p.sqft} sqft` : null].filter(Boolean).join(" · ")],
    [IndianRupee, p.budget],
    [Sparkles, p.style],
  ].filter(([, v]) => v);

  return (
    <div
      className="insp-lb"
      role="dialog"
      aria-modal="true"
      aria-label={p.name || "Design idea"}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touch.current == null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        touch.current = null;
        if (dx < -60 && index < pins.length - 1) onIndex(index + 1);
        if (dx > 60 && index > 0) onIndex(index - 1);
      }}
    >
      <button className="insp-lb-x" onClick={onClose} aria-label="Close"><X size={18} /></button>

      {index > 0 && (
        <button className="insp-lb-nav left" onClick={() => onIndex(index - 1)} aria-label="Previous">
          <ChevronLeft size={20} />
        </button>
      )}
      {index < pins.length - 1 && (
        <button className="insp-lb-nav right" onClick={() => onIndex(index + 1)} aria-label="Next">
          <ChevronRight size={20} />
        </button>
      )}

      <div className="insp-lb-scroll">
        <img className="insp-lb-img" src={pin.url} alt={p.name || ""} />

        <div className="insp-lb-body">
          <div className="insp-lb-tags">
            {pin.room && <span className="pill">{pin.room}</span>}
            {board && <span className="pill">in {board}</span>}
            <span className="insp-count">{index + 1} of {pins.length}</span>
          </div>

          {p.name && <h3>{p.name}</h3>}

          {facts.length > 0 && (
            <div className="insp-facts">
              {facts.map(([Icon, v], i) => <span key={i}><Icon size={13} /> {v}</span>)}
            </div>
          )}

          {p.summary && <p className="insp-lb-p">{p.summary}</p>}

          {Array.isArray(p.scope) && p.scope.length > 0 && (
            <>
              <p className="insp-lb-h">What was done</p>
              <ul className="insp-list">{p.scope.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </>
          )}

          {Array.isArray(p.materials) && p.materials.length > 0 && (
            <>
              <p className="insp-lb-h">Materials</p>
              <ul className="insp-list">{p.materials.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </>
          )}

          {p.review?.text && (
            <blockquote className="insp-quote">
              {p.review.rating ? (
                <span className="insp-stars" aria-label={`${p.review.rating} out of 5`}>
                  {Array.from({ length: p.review.rating }, (_, i) => <Star key={i} size={12} fill="currentColor" />)}
                </span>
              ) : null}
              {p.review.text}
              {p.review.author && <cite>— {p.review.author}</cite>}
            </blockquote>
          )}

          {more.length > 0 && (
            <>
              <p className="insp-lb-h">More like this</p>
              <div className="insp-more">
                {more.map((m) => (
                  <button
                    key={m.url}
                    onClick={() => {
                      const at = pins.findIndex((x) => x.url === m.url);
                      if (at >= 0) onIndex(at);
                    }}
                    style={{ backgroundImage: `url(${m.url})` }}
                    aria-label={m.project?.name || "Another idea"}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="insp-lb-acts">
        <button className={"btn" + (saved ? " primary" : "")} onClick={onToggle}>
          {saved ? <Check size={15} /> : <Heart size={15} />} {saved ? "Saved" : "Save"}
        </button>
        <button className="btn" onClick={onFile}><FolderPlus size={15} /> Board</button>
        <button
          className="btn"
          onClick={() => {
            onAsk?.(
              `I like this ${pin.room ? pin.room.toLowerCase() : "design"}` +
              (p.name ? ` from "${p.name}"` : "") +
              `. Could we do something like it in my home, and what would it add?`
            );
            onClose();
          }}
        >
          <Sparkles size={15} /> Veeha
        </button>
      </div>
    </div>
  );
}

/* ---- data ---------------------------------------------------------------- */

/**
 * The feed. NOT on the portal API base: design-ideas.php is served from
 * client.dleaps.com itself, takes no token, and exists precisely because the
 * marketing API sends no Access-Control-Allow-Origin. Do not "simplify" it
 * into a browser fetch of www.dleaps.com — that is what it works around.
 */
function useFeed() {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let dead = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fetch(FEED_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Feed ${r.status}`))))
      .then((j) => { if (!dead) setState({ loading: false, error: "", data: j }); })
      .catch((e) => { if (!dead) setState({ loading: false, error: e.message, data: null }); });
    return () => { dead = true; };
  }, [tick]);

  return { ...state, reload: () => setTick((n) => n + 1) };
}

/**
 * Saves and boards.
 *
 * The heart is optimistic: it fills the instant it is tapped and rolls back if
 * the server refuses. A heart that waits for a round trip does not feel like a
 * heart.
 */
function useSaves() {
  const [list, setList] = useState([]);
  const [boards, setBoards] = useState([]);
  const [urls, setUrls] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/portal/inspirations");
      const rows = r.saves || [];
      setList(rows);
      setBoards(r.boards || []);
      setUrls(new Set(rows.map((s) => s.image_url)));
    } catch { /* an empty wall beats an error message over a wall of pictures */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (pin) => {
    const url = pin.url;
    const on = urls.has(url);

    setUrls((prev) => {
      const next = new Set(prev);
      if (on) next.delete(url); else next.add(url);
      return next;
    });

    try {
      if (on) {
        await api.del(`/portal/inspirations?image_url=${encodeURIComponent(url)}`);
      } else {
        await api.post("/portal/inspirations", {
          image_url: url,
          room: pin.room || "",
          slug: pin.project?.slug || "",
          name: pin.project?.name || "",
          style: pin.project?.style || "",
        });
      }
      load();
    } catch {
      setUrls((prev) => {
        const next = new Set(prev);
        if (on) next.add(url); else next.delete(url);
        return next;
      });
    }
  }, [urls, load]);

  const createBoard = useCallback(async (name) => {
    try {
      const r = await api.post("/portal/inspirations/boards", { name });
      await load();
      return r;
    } catch { return null; }
  }, [load]);

  /** Filing something never hearted saves it first, so the picker is a save in
   *  its own right rather than something that silently does nothing. */
  const fileTo = useCallback(async (pin, boardId) => {
    try {
      if (!urls.has(pin.url)) {
        await api.post("/portal/inspirations", {
          image_url: pin.url,
          room: pin.room || "",
          slug: pin.project?.slug || "",
          name: pin.project?.name || "",
          style: pin.project?.style || "",
          board_id: boardId,
        });
      } else {
        await api.put("/portal/inspirations/boards", { image_url: pin.url, board_id: boardId });
      }
      await load();
    } catch { /* the picker closes either way; the next load tells the truth */ }
  }, [urls, load]);

  const renameBoard = useCallback(async (id, name) => {
    try { await api.put("/portal/inspirations/boards", { id, name }); await load(); } catch { /* ignore */ }
  }, [load]);

  const deleteBoard = useCallback(async (id) => {
    try { await api.del(`/portal/inspirations/boards?id=${id}`); await load(); } catch { /* ignore */ }
  }, [load]);

  return {
    list, boards, has: (u) => urls.has(u),
    toggle, createBoard, fileTo, renameBoard, deleteBoard, reload: load,
  };
}
